import React from 'react'
import { slugify } from '@utils/slugify'
import './PillarKeyResults.css'

interface CanvasMetricLite {
  id?: string
  name?: string
  target?: { value?: string; unit?: string; horizon?: string }
}

interface PillarLite {
  id?: string
  name?: string
  tactics?: Array<{ metrics?: CanvasMetricLite[] }>
}

interface Props {
  strategyMap: { pillars?: PillarLite[] } | undefined
  pillarId: string
}

function findPillar(strategyMap: Props['strategyMap'], pillarId: string): PillarLite | undefined {
  if (!pillarId) return undefined
  const pillars = strategyMap?.pillars ?? []
  // 1. Exact id match
  const byId = pillars.find(p => p.id === pillarId)
  if (byId) return byId
  // 2. Slugified-name fallback (avoid empty-string match against pillars with no name)
  const slugTarget = slugify(pillarId)
  return pillars.find(p => p.name && slugify(p.name) === slugTarget)
}

function formatTarget(target: CanvasMetricLite['target']): string {
  if (!target?.value) return ''
  const value = target.value
  const unit = target.unit
  if (!unit) return value
  // Naive duplicate-unit check: skip when value already contains a unit-like glyph
  // (% or $) or contains the unit substring case-insensitively.
  // Known limitation: bare-number values with unit="currency" or unit="count"
  // produce slightly awkward output ("1000 currency", "100 count"). Users can
  // clean these up in the Canvas Targets column where the AI badge surfaces;
  // the report is a read-out, not an editor (spec D4).
  if (/\d[%$]/.test(value)) return value
  if (value.toLowerCase().includes(unit.toLowerCase())) return value
  return `${value} ${unit}`
}

export const PillarKeyResults: React.FC<Props> = ({ strategyMap, pillarId }) => {
  const pillar = findPillar(strategyMap, pillarId)
  if (!pillar) return null

  // Flatten and dedupe metrics across all tactics in this pillar
  const seen = new Set<string>()
  const metrics: CanvasMetricLite[] = []
  for (const tactic of pillar.tactics ?? []) {
    for (const metric of tactic.metrics ?? []) {
      const key = metric.id || metric.name
      if (!key || seen.has(key)) continue
      seen.add(key)
      metrics.push(metric)
    }
  }

  if (metrics.length === 0) return null

  return (
    <ol className="pillar-key-results">
      {metrics.map(m => (
        <li key={m.id || m.name}>
          <strong>{m.name}</strong>: {formatTarget(m.target)}
        </li>
      ))}
    </ol>
  )
}
